import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Popover,
} from '@mui/material';
import { ShowChart as ChartIcon, CalendarToday as CalendarIcon } from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parse, parseISO } from 'date-fns';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TeamMember, User, PersonalRecord, RelayEventType, RelayResult } from '../../types';
import { teamService } from '../../services/teamService';
import { userService } from '../../services/userService';
import { activityService } from '../../services/activityService';
import { avatarService } from '../../services/avatarService';
import { relayService } from '../../services/relayService';
import { formatMinutesAsRaceTime } from '../../utils/prTimeFormat';
/** Same values as AthleteTagManager / BulkRaceResultsEntry event tags */
export const PROGRESSION_EVENT_TAGS = [
  { value: 'cross-country', label: 'Cross Country' },
  { value: '400m', label: '400m' },
  { value: '800m', label: '800m' },
  { value: '1600m', label: '1600m (Mile)' },
  { value: '3200m', label: '3200m (2 Mile)' },
  { value: '3k', label: '3k' },
  { value: '5K', label: '5K' },
  { value: '10K', label: '10K' },
  { value: 'Half Marathon', label: 'Half Marathon' },
  { value: 'Marathon', label: 'Marathon' },
  // Relay events (progression/leaderboards rank by overall relay time only)
  { value: '4x800', label: '4x800' },
  { value: '4x400', label: '4x400' },
  { value: '4x100', label: '4x100' },
  { value: 'DMR', label: 'DMR' },
] as const;

const RELAY_EVENT_TYPES: RelayEventType[] = ['4x800', '4x400', '4x100', 'DMR'];
const isRelayEventType = (eventType: string): eventType is RelayEventType =>
  RELAY_EVENT_TYPES.includes(eventType as RelayEventType);

const formatRaceTime = formatMinutesAsRaceTime;

function prRecordDateYmd(pr: PersonalRecord): string | null {
  try {
    const d = (pr.date as any)?.toDate?.() ?? new Date((pr.date as any)?.seconds * 1000);
    if (Number.isNaN(d.getTime())) return null;
    return format(d, 'yyyy-MM-dd');
  } catch {
    return null;
  }
}

/** Coach/head coach verified PR only */
function verifiedPrForEvent(
  prs: PersonalRecord[],
  eventType: string
): PersonalRecord | undefined {
  return prs.find((p) => p.eventType === eventType && p.isVerified === true);
}

export interface ProgressionPoint {
  date: string;
  label: string;
  raceTime: number;
  isPR: boolean;
  activityId: string;
}

function buildRelayProgressionSeries(
  relays: RelayResult[],
  athleteUserId: string
): ProgressionPoint[] {
  return relays
    .filter((r) => r.legAthleteIds.includes(athleteUserId))
    .sort((a, b) => a.date.localeCompare(b.date) || a.id.localeCompare(b.id))
    .map((r) => ({
      date: r.date,
      label: format(parseISO(r.date), 'MMM d'),
      raceTime: r.overallTimeMinutes,
      isPR: r.isVerified === true,
      activityId: `relay-${r.id}`,
    }));
}

function buildProgressionSeries(
  activities: import('../../types').DailyActivity[],
  eventType: string,
  verifiedPrForEvent: PersonalRecord | undefined,
  /** Saved PR row for this event (verified or not)—adds a point when date is in range and no duplicate time on calendar */
  anyPrForEvent: PersonalRecord | undefined,
  rangeStart: string,
  rangeEnd: string
): ProgressionPoint[] {
  const races = activities
    .filter((a) => activityIsRaceResultForEvent(a, eventType))
    .sort(
      (a, b) =>
        a.date.localeCompare(b.date) || (a.id || '').localeCompare(b.id || '')
    );

  const prDateYmd =
    verifiedPrForEvent && verifiedPrForEvent.eventType === eventType
      ? prRecordDateYmd(verifiedPrForEvent)
      : null;

  const fromRaces: ProgressionPoint[] = races.map((a) => {
    const t = a.raceTime as number;
    /** Same calendar day as coach-verified PR for this event (activity log is source of truth for time). */
    const isPR = !!(prDateYmd && prDateYmd === a.date);
    return {
      date: a.date,
      label: format(parseISO(a.date), 'MMM d'),
      raceTime: t,
      isPR,
      activityId: a.id || '',
    };
  });

  /* PR-only row: no race activity logged that day for this event (avoid duplicate times vs activity log). */
  if (anyPrForEvent?.eventType === eventType) {
    const prDate = prRecordDateYmd(anyPrForEvent);
    if (
      prDate &&
      prDate >= rangeStart &&
      prDate <= rangeEnd &&
      !fromRaces.some((p) => p.date === prDate)
    ) {
      fromRaces.push({
        date: prDate,
        label: format(parseISO(prDate), 'MMM d'),
        raceTime: anyPrForEvent.time,
        isPR: anyPrForEvent.isVerified === true,
        activityId: `pr-${anyPrForEvent.id}`,
      });
    }
  }

  return fromRaces.sort(
    (a, b) => a.date.localeCompare(b.date) || a.activityId.localeCompare(b.activityId)
  );
}

const EVENT_TYPE_TO_MILES: Record<string, number> = {
  '400m': 400 / 1609.34,
  '800m': 800 / 1609.34,
  '1600m': 1600 / 1609.34,
  '3200m': 3200 / 1609.34,
  '3k': 3000 / 1609.34,
  '5K': 5000 / 1609.34,
  '10K': 10000 / 1609.34,
  'Half Marathon': 13.1,
  Marathon: 26.2,
};

function milesRoughlyMatchesEvent(miles: number, eventType: string): boolean {
  const expected = EVENT_TYPE_TO_MILES[eventType];
  if (expected == null || expected <= 0) return false;
  return Math.abs(miles - expected) < 0.25;
}

/** Normalize UI / Firestore event labels so "1600m" matches "1600m (Mile)", etc. */
function canonicalProgressionEventKey(raw: string): string {
  const s = raw.trim().toLowerCase();
  if (!s) return '';
  if (s === '1600m' || (s.includes('1600') && s.includes('mile')) || s === 'mile') return '1600m';
  if (s === '3200m' || s.includes('3200') || (s.includes('2') && s.includes('mile'))) return '3200m';
  if (s === 'half marathon' || s.includes('half')) return 'Half Marathon';
  return raw.trim();
}

function storedEventMatchesSelected(storedTag: string, selectedEvent: string): boolean {
  const a = canonicalProgressionEventKey(storedTag);
  const b = canonicalProgressionEventKey(selectedEvent);
  if (a && b && a === b) return true;
  const t = storedTag.trim();
  const e = selectedEvent.trim();
  return t === e || t.toLowerCase() === e.toLowerCase();
}

function parseActivityRaceTimeMinutes(a: import('../../types').DailyActivity): number | null {
  const rt = a.raceTime as unknown;
  if (rt == null || rt === '') return null;
  const n = typeof rt === 'number' ? rt : Number(rt);
  return Number.isFinite(n) ? n : null;
}

/** Race log for this event: explicit raceEventType, or Race Day + distance matches event (bulk gap). */
function activityIsRaceResultForEvent(
  a: import('../../types').DailyActivity,
  eventType: string
): boolean {
  if (isRelayEventType(eventType)) return false;
  if (parseActivityRaceTimeMinutes(a) == null) return false;
  const tag = (a.raceEventType || '').trim();
  if (storedEventMatchesSelected(tag, eventType)) return true;
  if (a.runSubType === 'Race Day' && milesRoughlyMatchesEvent(Number(a.milesRun) || 0, eventType)) {
    return true;
  }
  return false;
}

function prRowForEvent(prs: PersonalRecord[], eventType: string): PersonalRecord | undefined {
  return prs.find((p) => p.eventType === eventType);
}

interface AthleteProgressionTabProps {
  teamId: string;
  initialEvent: string;
  initialRange: { start: Date; end: Date };
  initialAthleteUserId: string | null;
  onPrefsChange: (prefs: {
    progressionEvent: string;
    /** Omit when only event/athlete changed so From/To stay fixed in saved prefs */
    progressionDateStart?: string;
    progressionDateEnd?: string;
    progressionAthleteUserId: string | null;
  }) => void;
}

function isValidDate(d: Date): d is Date {
  return d instanceof Date && !isNaN(d.getTime());
}

/** Parse typed date string (e.g. 9/17/2025 or 09/17/2025). */
function parseDateInput(s: string): Date | null {
  const t = s.trim();
  if (!t) return null;
  const formats = ['M/d/yyyy', 'MM/dd/yyyy', 'M/dd/yyyy', 'MM/d/yyyy'];
  for (const f of formats) {
    try {
      const d = parse(t, f, new Date());
      if (!isNaN(d.getTime())) return d;
    } catch {
      /* try next */
    }
  }
  return null;
}

const AthleteProgressionTab: React.FC<AthleteProgressionTabProps> = ({
  teamId,
  initialEvent,
  initialRange,
  initialAthleteUserId,
  onPrefsChange,
}) => {
  const [eventTag, setEventTag] = useState(initialEvent);
  const [dateStart, setDateStart] = useState(() =>
    isValidDate(initialRange.start) ? initialRange.start : new Date()
  );
  const [dateEnd, setDateEnd] = useState(() =>
    isValidDate(initialRange.end) ? initialRange.end : new Date()
  );
  const [appliedDateStart, setAppliedDateStart] = useState(() =>
    isValidDate(initialRange.start) ? initialRange.start : new Date()
  );
  const [appliedDateEnd, setAppliedDateEnd] = useState(() =>
    isValidDate(initialRange.end) ? initialRange.end : new Date()
  );
  const [fromInput, setFromInput] = useState('');
  const [toInput, setToInput] = useState('');
  const [datePickerAnchor, setDatePickerAnchor] = useState<{ el: HTMLElement; which: 'from' | 'to' } | null>(null);
  const [loadingRoster, setLoadingRoster] = useState(true);
  const [rosterError, setRosterError] = useState('');
  const [athletes, setAthletes] = useState<
    {
      member: TeamMember;
      user: User;
      resultsInRange: number;
      points: ProgressionPoint[];
      bestTimeMinutes: number;
    }[]
  >([]);
  const [timeSort, setTimeSort] = useState<'fastest' | 'slowest'>('fastest');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(initialAthleteUserId);
  const [loadingChart, setLoadingChart] = useState(false);
  const [series, setSeries] = useState<ProgressionPoint[]>([]);
  const [chartAthleteName, setChartAthleteName] = useState('');

  const startStr = isValidDate(dateStart) ? format(dateStart, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd');
  const endStr = isValidDate(dateEnd) ? format(dateEnd, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd');
  const appliedStartStr = isValidDate(appliedDateStart) ? format(appliedDateStart, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd');
  const appliedEndStr = isValidDate(appliedDateEnd) ? format(appliedDateEnd, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd');

  const persist = useCallback(
    (overrides: Partial<Parameters<AthleteProgressionTabProps['onPrefsChange']>[0]>) => {
      const datesTouched =
        overrides.progressionDateStart !== undefined ||
        overrides.progressionDateEnd !== undefined;
      onPrefsChange({
        progressionEvent: overrides.progressionEvent ?? eventTag,
        ...(datesTouched
          ? {
              progressionDateStart: overrides.progressionDateStart ?? startStr,
              progressionDateEnd: overrides.progressionDateEnd ?? endStr,
            }
          : {}),
        progressionAthleteUserId:
          overrides.progressionAthleteUserId !== undefined
            ? overrides.progressionAthleteUserId
            : selectedUserId,
      });
    },
    [eventTag, startStr, endStr, selectedUserId, onPrefsChange]
  );

  const initialRangeKey = isValidDate(initialRange.start) && isValidDate(initialRange.end)
    ? `${format(initialRange.start, 'yyyy-MM-dd')}_${format(initialRange.end, 'yyyy-MM-dd')}`
    : 'default';
  useEffect(() => setEventTag(initialEvent), [initialEvent]);
  useEffect(() => {
    if (isValidDate(initialRange.start)) {
      setDateStart(initialRange.start);
      setAppliedDateStart(initialRange.start);
    }
    if (isValidDate(initialRange.end)) {
      setDateEnd(initialRange.end);
      setAppliedDateEnd(initialRange.end);
    }
  }, [initialRangeKey]);
  useEffect(() => setSelectedUserId(initialAthleteUserId), [initialAthleteUserId]);

  const loadRoster = useCallback(async () => {
    setLoadingRoster(true);
    setRosterError('');
    try {
      const allMembers = await teamService.getTeamMembers(teamId);
      const athleteMembers = allMembers.filter((m) => m.role !== 'coach' && m.role !== 'headCoach');
      const rows: {
        member: TeamMember;
        user: User;
        resultsInRange: number;
        points: ProgressionPoint[];
        bestTimeMinutes: number;
      }[] = [];

      if (isRelayEventType(eventTag)) {
        const relays = await relayService.getTeamRelayResults(
          teamId,
          eventTag,
          { start: appliedStartStr, end: appliedEndStr },
          5000
        );

        const nextRows: typeof rows = [];

        for (const member of [...athleteMembers].sort((a, b) => a.athleteId.localeCompare(b.athleteId))) {
          const points = buildRelayProgressionSeries(relays, member.athleteId);
          if (points.length === 0) continue;

          const u = await userService.getUserById(member.athleteId);
          if (!u) continue;

          nextRows.push({
            member,
            user: u,
            resultsInRange: points.length,
            points,
            bestTimeMinutes: Math.min(...points.map((p) => p.raceTime)),
          });
        }

        setAthletes(nextRows);
        setSelectedUserId((prev) =>
          prev && !nextRows.some((r) => r.member.athleteId === prev) ? null : prev
        );
        return;
      }

      for (const member of [...athleteMembers].sort((a, b) =>
        a.athleteId.localeCompare(b.athleteId)
      )) {
        const acts = await activityService.getUserActivitiesForProgression(
          member.athleteId,
          teamId,
          appliedStartStr,
          appliedEndStr
        );
        const prs = await avatarService.getUserPersonalRecords(member.athleteId, teamId);
        const verifiedPr = verifiedPrForEvent(prs, eventTag);
        const anyPr = prRowForEvent(prs, eventTag);
        const points = buildProgressionSeries(
          acts,
          eventTag,
          verifiedPr,
          anyPr,
          appliedStartStr,
          appliedEndStr
        );
        if (points.length === 0) continue;

        const u = await userService.getUserById(member.athleteId);
        if (!u) continue;
        const bestTimeMinutes = Math.min(...points.map((p) => p.raceTime));
        rows.push({
          member,
          user: u,
          resultsInRange: points.length,
          points,
          bestTimeMinutes,
        });
      }

      setAthletes(rows);
      setSelectedUserId((prev) =>
        prev && !rows.some((r) => r.member.athleteId === prev) ? null : prev
      );
    } catch (e) {
      console.error(e);
      setRosterError('Could not load roster.');
      setAthletes([]);
    } finally {
      setLoadingRoster(false);
    }
  }, [teamId, eventTag, appliedStartStr, appliedEndStr]);

  useEffect(() => {
    loadRoster();
  }, [loadRoster]);

  useEffect(() => {
    if (isValidDate(dateStart)) setFromInput(format(dateStart, 'M/d/yyyy'));
    if (isValidDate(dateEnd)) setToInput(format(dateEnd, 'M/d/yyyy'));
  }, [dateStart, dateEnd]);

  const commitFromInput = () => {
    const d = parseDateInput(fromInput);
    if (d) setDateStart(d);
    else if (isValidDate(dateStart)) setFromInput(format(dateStart, 'M/d/yyyy'));
  };
  const commitToInput = () => {
    const d = parseDateInput(toInput);
    if (d) setDateEnd(d);
    else if (isValidDate(dateEnd)) setToInput(format(dateEnd, 'M/d/yyyy'));
  };

  const applyDateRange = () => {
    if (!isValidDate(dateStart) || !isValidDate(dateEnd)) return;
    setAppliedDateStart(dateStart);
    setAppliedDateEnd(dateEnd);
    persist({
      progressionDateStart: format(dateStart, 'yyyy-MM-dd'),
      progressionDateEnd: format(dateEnd, 'yyyy-MM-dd'),
    });
  };

  const loadChart = async (userId: string, displayName: string) => {
    setSelectedUserId(userId);
    setChartAthleteName(displayName);
    setLoadingChart(true);
    persist({ progressionAthleteUserId: userId });
    try {
      if (isRelayEventType(eventTag)) {
        const relays = await relayService.getTeamRelayResults(
          teamId,
          eventTag,
          { start: appliedStartStr, end: appliedEndStr },
          5000
        );
        setSeries(buildRelayProgressionSeries(relays, userId));
      } else {
        const acts = await activityService.getUserActivitiesForProgression(
          userId,
          teamId,
          appliedStartStr,
          appliedEndStr
        );
        const prs = await avatarService.getUserPersonalRecords(userId, teamId);
        const verifiedPr = verifiedPrForEvent(prs, eventTag);
        const anyPr = prRowForEvent(prs, eventTag);
        setSeries(buildProgressionSeries(acts, eventTag, verifiedPr, anyPr, appliedStartStr, appliedEndStr));
      }
    } finally {
      setLoadingChart(false);
    }
  };

  const chartData = useMemo(() => {
    return series.map((p) => ({
      ...p,
      timeDisplay: formatRaceTime(p.raceTime),
    }));
  }, [series]);

  const displayedAthletes = useMemo(() => {
    const list = [...athletes];
    const byName = (a: (typeof athletes)[0], b: (typeof athletes)[0]) =>
      (a.user.displayName || a.user.email || '').localeCompare(
        b.user.displayName || b.user.email || '',
        undefined,
        { sensitivity: 'base' }
      );
    if (timeSort === 'fastest') {
      list.sort((a, b) => a.bestTimeMinutes - b.bestTimeMinutes || byName(a, b));
    } else {
      list.sort((a, b) => b.bestTimeMinutes - a.bestTimeMinutes || byName(a, b));
    }
    return list;
  }, [athletes, timeSort]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: { xs: 1, md: 2 } }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ChartIcon /> Athlete progression
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Lists every <strong>race log</strong> for this event in the date range (including bulk entries,
          even if teamId on the log differs). PR row fills gaps when no log matches that date. Verified =
          coach/bulk or{' '}
          <strong>Verify</strong> in PR editor. <strong>PR</strong> badge only on verified PRs. Sort by best
          time in range.
        </Typography>

        <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="progression-event-label">Event</InputLabel>
              <Select
                labelId="progression-event-label"
                label="Event"
                value={eventTag}
                onChange={(e) => {
                  const v = e.target.value;
                  setEventTag(v);
                  setSelectedUserId(null);
                  setSeries([]);
                  persist({ progressionEvent: v, progressionAthleteUserId: null });
                }}
              >
                {PROGRESSION_EVENT_TAGS.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              size="small"
              fullWidth
              label="From"
              placeholder="M/D/YYYY or click calendar"
              value={fromInput}
              onChange={(e) => setFromInput(e.target.value)}
              onBlur={commitFromInput}
              onKeyDown={(e) => e.key === 'Enter' && commitFromInput()}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={(e) => setDatePickerAnchor({ el: e.currentTarget, which: 'from' })}
                      aria-label="Pick date"
                    >
                      <CalendarIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              size="small"
              fullWidth
              label="To"
              placeholder="M/D/YYYY or click calendar"
              value={toInput}
              onChange={(e) => setToInput(e.target.value)}
              onBlur={commitToInput}
              onKeyDown={(e) => e.key === 'Enter' && commitToInput()}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={(e) => setDatePickerAnchor({ el: e.currentTarget, which: 'to' })}
                      aria-label="Pick date"
                    >
                      <CalendarIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Popover
            open={!!datePickerAnchor}
            onClose={() => setDatePickerAnchor(null)}
            anchorEl={datePickerAnchor?.el}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Box sx={{ p: 1 }}>
              <DatePicker
                value={datePickerAnchor?.which === 'from'
                  ? (isValidDate(dateStart) ? dateStart : null)
                  : (isValidDate(dateEnd) ? dateEnd : null)}
                onChange={(d) => {
                  if (!d || !isValidDate(d)) return;
                  if (datePickerAnchor?.which === 'from') {
                    setDateStart(d);
                    setFromInput(format(d, 'M/d/yyyy'));
                  } else {
                    setDateEnd(d);
                    setToInput(format(d, 'M/d/yyyy'));
                  }
                  setDatePickerAnchor(null);
                }}
                slotProps={{ textField: { size: 'small' } }}
              />
            </Box>
          </Popover>
          <Grid size={{ xs: 12, sm: 2 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={applyDateRange}
              disabled={loadingRoster || !isValidDate(dateStart) || !isValidDate(dateEnd)}
            >
              Apply date range
            </Button>
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="progression-sort-label">Sort by time</InputLabel>
              <Select
                labelId="progression-sort-label"
                label="Sort by time"
                value={timeSort}
                onChange={(e) => setTimeSort(e.target.value as 'fastest' | 'slowest')}
              >
                <MenuItem value="fastest">Fastest first (best in range)</MenuItem>
                <MenuItem value="slowest">Slowest first (best in range)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 1 }}>
            <Button variant="outlined" fullWidth onClick={() => loadRoster()} disabled={loadingRoster}>
              Refresh
            </Button>
          </Grid>
        </Grid>

        {rosterError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {rosterError}
          </Alert>
        )}

        {loadingRoster ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : athletes.length === 0 ? (
          <Alert severity="info">
            {isRelayEventType(eventTag) ? (
              <>
                No relay results for &quot;{eventTag}&quot; between {appliedStartStr} and {appliedEndStr}.
              </>
            ) : (
              <>
                No one on this team has a Race Day log or a PR dated for &quot;{eventTag}&quot; between{' '}
                {appliedStartStr} and {appliedEndStr}.
              </>
            )}
          </Alert>
        ) : (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: selectedUserId ? 5 : 12 }}>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Athlete</TableCell>
                      <TableCell>Times in range ({eventTag})</TableCell>
                      <TableCell align="center">Best</TableCell>
                      <TableCell align="right">#</TableCell>
                      <TableCell align="right">Chart</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayedAthletes.map((row) => (
                      <TableRow
                        key={row.member.id}
                        selected={selectedUserId === row.member.athleteId}
                        hover
                      >
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {row.user.displayName || row.user.email}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 320 }}>
                          <Typography variant="body2" component="span" sx={{ lineHeight: 1.6 }}>
                            {row.points.map((p, i) => (
                              <React.Fragment key={p.activityId + i}>
                                {i > 0 ? ' · ' : null}
                                <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                                  {p.label}: <strong>{formatRaceTime(p.raceTime)}</strong>
                                  {p.isPR ? (
                                    <Box
                                      component="span"
                                      sx={{
                                        ml: 0.5,
                                        fontSize: '0.7rem',
                                        color: 'warning.dark',
                                        fontWeight: 700,
                                      }}
                                    >
                                      {isRelayEventType(eventTag) ? 'Relay' : 'PR'}
                                    </Box>
                                  ) : null}
                                </Box>
                              </React.Fragment>
                            ))}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <strong>{formatRaceTime(row.bestTimeMinutes)}</strong>
                        </TableCell>
                        <TableCell align="right">{row.resultsInRange}</TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant={selectedUserId === row.member.athleteId ? 'contained' : 'text'}
                            onClick={() =>
                              loadChart(
                                row.member.athleteId,
                                row.user.displayName || row.user.email || 'Athlete'
                              )
                            }
                          >
                            Chart
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            {selectedUserId && (
              <Grid size={{ xs: 12, md: 7 }}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    {chartAthleteName} — {eventTag}
                  </Typography>
                  {loadingChart ? (
                    <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                      <CircularProgress />
                    </Box>
                  ) : chartData.length === 0 ? (
                    <Typography color="text.secondary">
                      {isRelayEventType(eventTag)
                        ? 'No relay entries for this athlete in the selected date range.'
                        : 'No race logs or PR row date in range for this event.'}
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        {isRelayEventType(eventTag)
                          ? 'Verified relay = coach-entered relay (coach/bulk verified).'
                          : 'PR badge = coach-verified PR matching that performance (date + time).'}
                      </Typography>
                      <ResponsiveContainer width="100%" height={340}>
                        <LineChart data={chartData} margin={{ top: 12, right: 12, left: 8, bottom: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="label" />
                          <YAxis
                            domain={['auto', 'auto']}
                            tickFormatter={(v) => formatRaceTime(Number(v))}
                          />
                          <Tooltip
                            content={({ active, payload }) => {
                              if (!active || !payload?.[0]) return null;
                              const p = payload[0].payload as ProgressionPoint & { timeDisplay: string };
                              return (
                                <Paper sx={{ px: 1.5, py: 1 }} elevation={3}>
                                  <Typography variant="body2" fontWeight="bold">
                                    {p.label} · {p.timeDisplay}
                                  </Typography>
                                  {p.isPR && (
                                    <Typography variant="caption" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                      <span aria-hidden>🏆</span> {isRelayEventType(eventTag) ? 'Relay' : 'PR'} — {p.timeDisplay}
                                    </Typography>
                                  )}
                                </Paper>
                              );
                            }}
                          />
                          <Line
                            type="monotone"
                            dataKey="raceTime"
                            stroke="#1976d2"
                            strokeWidth={2}
                            dot={(props: any) => {
                              const { cx, cy, payload } = props;
                              if (payload?.isPR) {
                                return (
                                  <g transform={`translate(${cx},${cy})`}>
                                    <circle r={14} fill="#F9A825" stroke="#F57F17" strokeWidth={2} />
                                    <text
                                      textAnchor="middle"
                                      dy={4}
                                      fill="#fff"
                                      fontSize={9}
                                      fontWeight="bold"
                                    >
                                      {isRelayEventType(eventTag) ? 'Relay' : 'PR'}
                                    </text>
                                  </g>
                                );
                              }
                              return (
                                <circle cx={cx} cy={cy} r={5} fill="#1976d2" stroke="#fff" strokeWidth={1} />
                              );
                            }}
                            activeDot={{ r: 10 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </>
                  )}
                </Paper>
              </Grid>
            )}
          </Grid>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default AthleteProgressionTab;
